import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../services/firebase';
import AppNavBar from './AppNavBar';
import ArchieChat from './ArchieChat';
import { useModal } from '../contexts/ModalContext';

const PlannerLibraryPage = () => {
  const [user] = useAuthState(auth);
  const [lessons, setLessons] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterYearGroup, setFilterYearGroup] = useState('');
  const { showAlert, showConfirm } = useModal();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchLessonsAndResources();
    }
  }, [user]);

  const fetchLessonsAndResources = async () => {
    try {
      const lessonsQuery = query(
        collection(db, 'userLessons'),
        where('userId', '==', user.uid)
      );
      const lessonsSnapshot = await getDocs(lessonsQuery);
      const lessonsList = lessonsSnapshot.docs.map(doc => ({
        id: doc.id,
        type: 'lesson',
        ...doc.data()
      }));
      
      setLessons(lessonsList);
      
      try {
        const resourcesQuery = query(
          collection(db, 'userResources'),
          where('userId', '==', user.uid)
        );
        const resourcesSnapshot = await getDocs(resourcesQuery);
        const resourcesList = resourcesSnapshot.docs.map(doc => ({
          id: doc.id,
          type: 'resource',
          ...doc.data()
        }));
        
        setResources(resourcesList);
      } catch (resourceError) {
        console.log('Resources collection not found or no permissions - this is normal for new setups');
        setResources([]);
      }
      
    } catch (error) {
      console.error('Error fetching lessons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    const confirmed = await showConfirm('Are you sure you want to delete this lesson plan?');
    if (confirmed) {
      try {
        await deleteDoc(doc(db, 'userLessons', lessonId));
        setLessons(lessons.filter(lesson => lesson.id !== lessonId));
      } catch (error) {
        console.error('Error deleting lesson:', error);
        await showAlert('Error deleting lesson. Please try again.', 'Error');
      }
    }
  };

  const handleDeleteResource = async (resourceId) => {
    const confirmed = await showConfirm('Are you sure you want to delete this resource?');
    if (confirmed) {
      try {
        await deleteDoc(doc(db, 'userResources', resourceId));
        setResources(resources.filter(resource => resource.id !== resourceId));
      } catch (error) {
        console.error('Error deleting resource:', error);
        await showAlert('Error deleting resource. Please try again.', 'Error');
      }
    }
  };

  const handleDuplicateLesson = (lesson) => {
    navigate('/planner/form', { 
      state: { 
        duplicateData: {
          subject: lesson.metadata.subject,
          yearGroup: lesson.metadata.yearGroup,
          topic: lesson.metadata.topic,
          duration: lesson.metadata.duration,
          learningObjectives: lesson.metadata.learningObjectives,
          priorKnowledge: lesson.metadata.priorKnowledge,
          resources: lesson.metadata.resources,
          differentiation: lesson.metadata.differentiation
        }
      }
    });
  };

  const handleDuplicateResource = (resource) => {
    navigate('/planner/resources', { 
      state: { 
        duplicateData: {
          resourceType: resource.metadata.resourceType,
          subject: resource.metadata.subject,
          yearGroup: resource.metadata.yearGroup,
          topic: resource.metadata.topic,
          requirements: resource.metadata.requirements,
          specialRequirements: resource.metadata.specialRequirements
        }
      }
    });
  };

  const handleViewLesson = (lesson) => {
    navigate('/planner/result', { 
      state: { 
        lessonPlan: lesson.lessonPlan,
        formData: lesson.metadata,
        fromLibrary: true
      }
    });
  };

  const handleViewResource = (resource) => {
    navigate('/planner/resources/result', { 
      state: { 
        resourceContent: resource.resourceContent,
        formData: resource.metadata,
        selectedImage: resource.metadata.selectedImage,
        fromLibrary: true
      }
    });
  };

  const filterItems = (items, isLesson = true) => {
    return items.filter(item => {
      const topic = isLesson ? item.metadata.topic : item.metadata.topic;
      const subject = isLesson ? item.metadata.subject : item.metadata.subject;
      
      const matchesSearch = topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           subject.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSubject = filterSubject === '' || subject === filterSubject;
      const matchesYearGroup = filterYearGroup === '' || item.metadata.yearGroup === filterYearGroup;
      
      return matchesSearch && matchesSubject && matchesYearGroup;
    });
  };

  const filteredLessons = filterItems(lessons, true);
  const filteredResources = filterItems(resources, false);

  const allItems = [...lessons, ...resources];
  const uniqueSubjects = [...new Set(allItems.map(item => item.metadata.subject))];
  const uniqueYearGroups = [...new Set(allItems.map(item => item.metadata.yearGroup))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
        <AppNavBar currentPage="planner" />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-400 mx-auto mb-4"></div>
            <p className="text-ink-900 text-lg">Loading your teaching library...</p>
          </div>
        </div>
      </div>
    );
  }

  const EmptySection = ({ title, description, buttonText, onClick }) => (
    <div className="text-center py-12">
      <div className="mx-auto h-20 w-20 text-sand-300 mb-4">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} 
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-ink-800 mb-2">{title}</h3>
      <p className="text-ink-600 text-sm mb-4">{description}</p>
      <button
        onClick={onClick}
        className="bg-coral-300 text-white px-4 py-2 rounded-lg hover:bg-coral-500 transition-all duration-300 font-medium text-sm"
      >
        {buttonText}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="planner" />
      
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-coral-500 leading-tight mb-4 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
              📚 My Teaching Library
            </h1>
            <p className="text-lg text-ink-700">
              {lessons.length} lesson plans • {resources.length} resources
            </p>
          </div>

          {/* Create New Buttons */}
          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={() => navigate('/planner/form')}
              className="bg-coral-300 text-white px-6 py-3 rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold shadow-lg transform hover:-translate-y-1"
            >
              <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              Create New Lesson
            </button>
            <button
              onClick={() => navigate('/planner/resources')}
              className="bg-coral-300 text-white px-6 py-3 rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold shadow-lg transform hover:-translate-y-1"
            >
              <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              Create New Resource
            </button>
          </div>

          {/* Filters Card */}
          <div className="bg-white rounded-lg border-4 border-ink-900 shadow-xl mb-8 p-6">
            <h2 className="text-xl font-semibold text-ink-800 mb-4">Filter & Search</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Search lessons & resources
                </label>
                <input
                  type="text"
                  placeholder="Search by topic or subject..."
                  className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500 transition-colors"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Filter by Subject
                </label>
                <select
                  className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500 transition-colors"
                  value={filterSubject}
                  onChange={(e) => setFilterSubject(e.target.value)}
                >
                  <option value="">All Subjects</option>
                  {uniqueSubjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Filter by Year Group
                </label>
                <select
                  className="w-full px-4 py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500 transition-colors"
                  value={filterYearGroup}
                  onChange={(e) => setFilterYearGroup(e.target.value)}
                >
                  <option value="">All Year Groups</option>
                  {uniqueYearGroups.map(yearGroup => (
                    <option key={yearGroup} value={yearGroup}>{yearGroup}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* My Lessons Section */}
            <div className="bg-white rounded-lg border-4 border-ink-900 shadow-xl p-6">
              <div className="flex items-center mb-6">
                <svg className="w-6 h-6 text-coral-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                </svg>
                <h2 className="text-xl font-semibold text-ink-800">My Lessons</h2>
                <span className="ml-auto bg-coral-100 text-coral-800 text-sm px-2 py-1 rounded-full">
                  {filteredLessons.length}
                </span>
              </div>

              {filteredLessons.length === 0 ? (
                <EmptySection
                  title="No lessons found"
                  description={searchTerm || filterSubject || filterYearGroup ? "Try adjusting your filters" : "Create your first lesson plan"}
                  buttonText="Create Lesson"
                  onClick={() => navigate('/planner/form')}
                />
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {filteredLessons.map((lesson) => (
                    <div key={lesson.id} className="border-4 border-ink-900 rounded-lg p-4 hover:shadow-md transition-shadow bg-sand-50">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-md font-semibold text-ink-900 line-clamp-1">
                          {lesson.metadata.topic}
                        </h3>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-1 mb-3 text-xs text-ink-600">
                        <div><span className="font-medium">Subject:</span> {lesson.metadata.subject}</div>
                        <div><span className="font-medium">Year:</span> {lesson.metadata.yearGroup}</div>
                        <div><span className="font-medium">Duration:</span> {lesson.metadata.duration}</div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewLesson(lesson)}
                          className="flex-1 bg-coral-300 text-white py-2 px-3 rounded text-xs hover:bg-coral-500 transition-colors font-medium"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDeleteLesson(lesson.id)}
                          className="flex-1 bg-coral-300 text-white py-2 px-3 rounded text-xs hover:bg-coral-500 transition-colors font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* My Resources Section */}
            <div className="bg-white rounded-lg border-4 border-ink-900 shadow-xl p-6">
              <div className="flex items-center mb-6">
                <svg className="w-6 h-6 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <h2 className="text-xl font-semibold text-ink-800">My Resources</h2>
                <span className="ml-auto bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full">
                  {filteredResources.length}
                </span>
              </div>

              {filteredResources.length === 0 ? (
                <EmptySection
                  title="No resources found"
                  description={searchTerm || filterSubject || filterYearGroup ? "Try adjusting your filters" : "Create your first resource"}
                  buttonText="Create Resource"
                  onClick={() => navigate('/planner/resources')}
                />
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {filteredResources.map((resource) => (
                    <div key={resource.id} className="border-4 border-ink-900 rounded-lg p-4 hover:shadow-md transition-shadow bg-sand-50">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-md font-semibold text-ink-900 line-clamp-1">
                          {resource.metadata.topic}
                        </h3>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {resource.metadata.resourceType}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-1 mb-3 text-xs text-ink-600">
                        <div><span className="font-medium">Subject:</span> {resource.metadata.subject}</div>
                        <div><span className="font-medium">Year:</span> {resource.metadata.yearGroup}</div>
                        {resource.metadata.selectedImage && (
                          <div><span className="font-medium">Image:</span> ✓ Included</div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewResource(resource)}
                          className="flex-1 bg-coral-300 text-white py-2 px-3 rounded text-xs hover:bg-coral-500 transition-colors font-medium"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDeleteResource(resource.id)}
                          className="flex-1 bg-coral-300 text-white py-2 px-3 rounded text-xs hover:bg-coral-500 transition-colors font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ArchieChat />
    </div>
  );
};

export default PlannerLibraryPage;